<script setup lang="ts">
import {computed, type PropType, ref} from 'vue';
import {ElInput, ElInputNumber, ElOption, ElSelect} from 'element-plus';
import {CabDashboardKpiTarget, CabKPI, CabOrganization, DashboardReeksEnum_name, DashboardTargetRuleEnum, DashboardTargetRuleEnum_name, uuidOf} from 'common';
import {DASHBOARD_TARGET_CHANGE_EMIT, DASHBOARD_TARGET_REMOVE_EMIT} from '@/base/cabConstants';
import DeleteButton from '@/views/icons/DeleteIcon.vue';

const emits = defineEmits([DASHBOARD_TARGET_REMOVE_EMIT, DASHBOARD_TARGET_CHANGE_EMIT]);
const props = defineProps({
    target: {
        type    : Object as PropType<CabDashboardKpiTarget>,
        required: true,
    },
    allOrganizations: {
        type    : Array as PropType<CabOrganization[]>,
        required: true,
    },
    allKpis: { // only kpis with a dashboardReeks
        type    : Array as PropType<CabKPI[]>,
        required: true,
    },
});

const target          = ref<CabDashboardKpiTarget>(props.target);
const organizationId  = computed<string | undefined>({
    get: () => target.value.organization[0] ? uuidOf(target.value.organization[0]) : undefined,
    set: (id: string | undefined) => {
        const org: CabOrganization | undefined = props.allOrganizations.find(o => uuidOf(o) === id);
        target.value.organization              = org ? [org] : [];
        onChange();
    },
});
const kpiId           = computed<string | undefined>({
    get: () => target.value.kpi[0] ? uuidOf(target.value.kpi[0]) : undefined,
    set: (id: string | undefined) => {
        const kpi: CabKPI | undefined = props.allKpis.find(k => uuidOf(k) === id);
        target.value.kpi              = kpi ? [kpi] : [];
        onChange();
    },
});
const isVasteWaarde   = computed<boolean>(() => target.value.rule === DashboardTargetRuleEnum.vasteWaarde);

function kpiLabel(kpi: CabKPI): string {
    return `${kpi.name} (${DashboardReeksEnum_name(kpi.dashboardReeks)})`;
}

function onChange() {
    emits(DASHBOARD_TARGET_CHANGE_EMIT, target.value);
}

function onDelete() {
    emits(DASHBOARD_TARGET_REMOVE_EMIT, target.value);
}
</script>

<template>
    <div class="flex flex-row items-center gap-2 w-full font-medium m-0.5 p-1 bg-yellow-100 text-gray-900">
        <el-select v-model="organizationId" placeholder="organisatie" class="w-56" filterable>
            <el-option v-for="o in allOrganizations" :key="uuidOf(o)" :value="uuidOf(o)" :label="o.name"/>
        </el-select>
        <el-select v-model="kpiId" placeholder="kpi" class="flex-1" filterable>
            <el-option v-for="k in allKpis" :key="uuidOf(k)" :value="uuidOf(k)" :label="kpiLabel(k)"/>
        </el-select>
        <el-select v-model="target.rule" placeholder="regel" class="w-72" @change="onChange">
            <el-option v-for="r in Object.values(DashboardTargetRuleEnum)" :key="r" :value="r" :label="DashboardTargetRuleEnum_name(r)"/>
        </el-select>
        <el-input-number v-if="isVasteWaarde" v-model="target.value" :controls="false" placeholder="waarde" class="w-28" @change="onChange"/>
        <el-input v-model="target.omschrijving" placeholder="omschrijving van het target" class="w-80" @change="onChange"/>
        <DeleteButton @click="onDelete()"/>
    </div>
</template>
